<template>
  <div>
    <div v-if="showCookiesTooltip" class="cookies-button"
         v-b-tooltip.hover.right :title="$t('Cookies settings')" @click="openModal">
      <i class="fa fa-cookie-bite"></i>
    </div>
    <b-modal :id="id"
             lazy
             centered
             ok-variant="ls"
             size="lg"
             :title="$t('Cookies')"
             :cancel-title="$t('Not accept')"
             footer-class="justify-content-between"
             :ok-title="$t('Understand and accept')"
             @cancel="dismiss"
             @ok="allowAndBootstrap()"
             hide-header
             @hidden="onHidden"
             no-close-on-backdrop
             no-close-on-esc
    >
      <div class="d-flex">
        <img src="@/assets/cookie.svg" alt="" class="cookie mx-auto mt-4">
      </div>
      <div class="d-flex flex-column mt-5">
        <h4 class="mx-auto"><strong>{{$t('Cookies info')}}</strong></h4>
        <p class="mt-4 text-justify">
          {{$t('Website uses cookies')}} {{ trackers.join(' i ') }}. {{$t('More info')}}
          <a href="#" @click="openPrivacyPolicy">{{$t('Privacy policy link')}} </a>. {{$t('If you agree or not')}}
        </p>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {mapActions, mapState} from "vuex";
import {bootstrap, setOptions} from "vue-gtag";
import {PRIVACY_POLICY} from "@/utils/PageModalTypes";
import Cookies from "js-cookie";
import {bootstrap as fpBootstrap} from '@/utils/FacebookPixel';
import {
  acceptConversionAnalytics,
  configureConversionAnalytics,
  declineConversionAnalytics,
  notifyConversionTrackersReady,
} from '@/utils/ConversionAnalytics';

export default {
  name: "CookiesModal",
  data() {
    return {
      id: 'cookies-modal',
      showCookiesTooltip: false,
      refreshOnClose: false,
      initialized: false,
      pixelInitialized: false,
      pixelMatchedEmail: '',
    }
  },
  mounted() {
    this.clearCookies();

    //wait for settings
    this.$store.subscribe((mutation) => {
      if (mutation.type === 'SET_SETTINGS') {
        this.initialize();
      }
    })
  },
  methods: {
    ...mapActions([
      'setPageModal'
    ]),
    initialize() {
      if (this.initialized) {
        return;
      }

      this.initialized = true;

      configureConversionAnalytics({
        ga4Id: this.analytics.ga4,
        gtmId: this.analytics.gtm,
        pixelId: this.analytics.fbp,
        accepted: this.allowCookies,
        dismissed: this.dismissCookies,
      });

      if (!this.analytics.ga4 && !this.analytics.fbp) {
        return;
      }

      this.showCookiesTooltip = true;

      if (this.dismissCookies) {
        return; //cookies dismissed skip
      }

      if (this.allowCookies) {
        this.allowAndBootstrap({
          persistConsent: false,
          trackCurrentPage: false,
        });
        return;
      }

      this.$bvModal.show('cookies-modal')
    },
    dismiss() {
      localStorage.removeItem(this.allowStorageKey);
      localStorage.setItem(this.dismissStorageKey, 'true');
      declineConversionAnalytics();
      this.clearCookies();
    },
    allowAndBootstrap({
      persistConsent = true,
      trackCurrentPage = true,
    } = {}) {
      this.refreshOnClose = false;
      localStorage.removeItem(this.dismissStorageKey);

      if (persistConsent) {
        localStorage.setItem(this.allowStorageKey, 'true');
      }

      acceptConversionAnalytics();

      if (this.analytics.ga4) {
        const configId = this.analytics.ga4;
        const includes = [];

        setOptions({config: {id: configId,}, includes});
        Promise.resolve(bootstrap()).then(() => {
          if (trackCurrentPage) {
            this.trackCurrentGa4PageView();
          }
          notifyConversionTrackersReady();
        });
      }

      if (this.analytics.fbp) {
        this.initFbPixel();
      }
    },
    initFbPixel() {
      if (this.pixelInitialized) {
        return;
      }

      this.pixelInitialized = true;

      fpBootstrap()
          .then(() => this.$analytics.fbq.init(this.analytics.fbp))
          .then(() => {
            window.fbq('track', 'PageView');
            this.enrichFbPixel();
            notifyConversionTrackersReady();
          });
    },
    trackCurrentGa4PageView() {
      const titleElement = document.querySelector('h1.m-subheader__title');

      this.$gtag.pageview({
        page_title: titleElement ? titleElement.innerHTML : this.$route.name,
        page_path: this.$route.path,
        page_location: window.location.href,
      });
    },
    enrichFbPixel() {
      const email = typeof this.user.email === 'string' ? this.user.email.trim() : '';
      if (!this.pixelInitialized || !this.isAuthorized || !email ||
          this.pixelMatchedEmail === email) {
        return;
      }

      this.$analytics.fbq.init(this.analytics.fbp, {
        em: email,
      });
      this.pixelMatchedEmail = email;
    },
    openPrivacyPolicy() {
      this.setPageModal({
        show: true,
        type: PRIVACY_POLICY,
        title: this.$t('Privacy policy'),
        content: ''
      });
    },
    openModal() {
      this.refreshOnClose = true;
      this.$bvModal.show(this.id);
    },
    onHidden() {
      if (this.refreshOnClose) {
        window.location.reload();
      }
    },
    clearCookies() {
      if (this.allowCookies) {
        return;
      }

      const cookies = Cookies.get();
      const domains = [
        '.app.loyaltystarter.io',
        'app.loyaltystarter.io',
        '.loyaltystarter.io',
        '.' + location.hostname.split('.').slice(-2).join('.'),
      ];

      for (const [key,] of Object.entries(cookies)) {
        const isTrackingCookie = key.startsWith('_ga') ||
          key.startsWith('_gid') ||
          key.startsWith('_gat') ||
          key.startsWith('_gcl') ||
          key.startsWith('_fbp') ||
          key.startsWith('_fbc');

        if (!isTrackingCookie) {
          continue;
        }

        Cookies.remove(key, {path: '/'});
        for (const domain of domains) {
          Cookies.remove(key, {path: '/', domain})
        }
      }
    }
  },
  computed: {
    ...mapState({
      analytics: state => state.settings.analytics,
      entityUuid: state => state.auth.entity.uuid,
      isAuthorized: state => state.auth.isAuthorized,
      user: state => state.auth.user,
    }),
    dismissStorageKey() {
      return `ls-dismiss-cookies-${this.entityUuid}`;
    },
    dismissCookies() {
      return localStorage.getItem(this.dismissStorageKey) === 'true';
    },
    allowStorageKey() {
      return `ls-allow-cookies-${this.entityUuid}`;
    },
    allowCookies() {
      return localStorage.getItem(this.allowStorageKey) === 'true';
    },
    trackers() {
      let trackers = [];

      if (this.analytics.ga4) {
        trackers.push(this.$t('Analytical') + ' (Google Analytics)')
      }
      if (this.analytics.gtm) {
        trackers.push(this.$t('Analytical') + ' (Google Tag Manager)')
      }

      if (this.analytics.fbp) {
        trackers.push(this.$t('Marketing') + ' (Facebook Piksel)')
      }

      return trackers;
    }
  },
  watch: {
    'user.email'() {
      this.enrichFbPixel();
    }
  },
}
</script>

<style scoped>
.cookie {
  width: 100%;
  max-width: 150px;
  height: 100%;
  max-height: 150px;
}

.cookies-button {
  position: absolute;
  bottom: 5px;
  left: 5px;
  padding: 2px 4px;
  border-radius: 4px;
  background: rgba(0, 0, 0, 0.2);
  color: #fff;
}

@media (min-width: 768px) {
  .cookies-button > i {
    font-size: 20px;
  }
  .cookies-button {
    padding: 4px 5px;
  }
}

.cookies-button:hover {
  cursor: pointer;
  background: rgba(0, 0, 0, 0.15);
}
</style>
